import { API_BASE } from './api-base';

// Reports ONE stack trace when a data call fires far more often than any screen
// could legitimately need. A healthy list fetches once or twice per mount, so a
// rate this high is always an unintended retrigger — and the stack is the only
// thing that can name it: server logs see identical requests with no caller, and
// the retrigger has so far proved unreproducible on a healthy account.
//
// Measurement only. It never changes the call it wraps, never throws, and stays
// silent below the threshold.

const WINDOW_MS = 10_000;
const THRESHOLD = 15;
// The loop re-fires for as long as the tab lives; one report a minute is enough
// signal without the probe adding to the traffic it is describing.
const REPORT_INTERVAL_MS = 60_000;
// /api/client-error clamps each field to 1000 chars. A raw frame spends ~110 of them
// on the bundle URL, so the first report fitted only React Query's innards and cut off
// exactly where the caller is named. Compacting the URL away buys ~4x the frames.
const MAX_STACK = 900;
// Frames belonging to the probe itself, which would otherwise head every report.
const OWN_FRAMES = 3;
const BUNDLE_URL = /https?:\/\/[^ )]*\/_next\/static\/(?:chunks\/)?/g;

interface Call {
  at: number;
  key: string;
}

const recentCalls = new Map<string, Call[]>();
const lastReportAt = new Map<string, number>();

/**
 * The Next route PATTERN (`/workspace/[slug]`), never the live URL — a pattern
 * carries no slug, id or query value, which is where personal data would be.
 */
function routePattern(): string {
  try {
    return (window as unknown as { next?: { router?: { pathname?: string } } }).next?.router?.pathname ?? '';
  } catch {
    return '';
  }
}

function callerFrames(): string {
  const stack = new Error().stack ?? '';
  return stack
    .split('\n')
    .slice(OWN_FRAMES)
    .map((frame) => frame.replace(BUNDLE_URL, '').replace(/^\s*at\s*/, '').trim())
    .filter(Boolean)
    .join(' | ')
    .slice(0, MAX_STACK);
}

function report(label: string, calls: Call[], spanMs: number, detail: string): void {
  // How many distinct cache keys the burst spans separates a single query being
  // re-driven from a screen legitimately fanning out across many of them.
  const keys = Array.from(new Set(calls.map((call) => call.key)));
  void fetch(`${API_BASE}/api/client-error`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    keepalive: true,
    body: JSON.stringify({
      kind: 'runaway-fetch',
      message:
        `label=${label} calls=${calls.length} inMs=${spanMs} ` +
        `keys=${keys.length} key=${keys.slice(0, 3).join(',')}` +
        (detail ? ` ${detail}` : ''),
      stack: callerFrames(),
      route: routePattern(),
    }),
  }).catch(() => {});
}

/**
 * Call at the top of a data fetcher to measure how often it is being driven.
 * `key` identifies the cache entry being fetched, so the report can say whether one
 * entry is being re-driven or many are in flight at once. `describe` is read ONLY when a
 * report is about to be sent, so a caller can attach state that costs something to compute
 * without paying for it on every healthy fetch.
 */
export function noteFetch(label: string, key = 'default', describe?: () => string): void {
  if (typeof window === 'undefined') return;
  try {
    const now = Date.now();
    const recent = (recentCalls.get(label) ?? []).filter((call) => now - call.at < WINDOW_MS);
    recent.push({ at: now, key });
    recentCalls.set(label, recent);

    if (recent.length < THRESHOLD) return;
    if (now - (lastReportAt.get(label) ?? 0) < REPORT_INTERVAL_MS) return;
    lastReportAt.set(label, now);
    let detail = '';
    try {
      detail = describe?.() ?? '';
    } catch {
      /* a caller's own describe must not cost us the report */
    }
    report(label, recent, now - recent[0].at, detail);
  } catch {
    /* a probe that throws would break the call it is measuring */
  }
}

/** Test seam: the probe's counters are module state. */
export function resetRunawayFetchProbe(): void {
  recentCalls.clear();
  lastReportAt.clear();
}
